import { csvEscape, filterLabelFromList, safeCsvSlug, triggerCsvDownload } from './perfHubCsvUtils';
import { COMMENT_FILTERS } from './perfHubCommentFilter';

function rowVal(c, key) {
  const v = c?.[key];
  return v == null ? '' : v;
}

function normHandle(h) {
  return String(h || '')
    .trim()
    .toLowerCase()
    .replace(/^@/, '');
}

/** Format per-creator buyer questions for CSV (from row or campaign top_questions fallback). */
export function formatBuyerQuestionsForCsv(creator, commentIntelligence) {
  const fromRow = creator?.buyer_questions;
  if (Array.isArray(fromRow) && fromRow.length > 0) {
    return fromRow
      .map((q) => {
        const text = String(q.text || '').replace(/\|/g, '/').trim();
        const status = q.answered ? 'answered' : 'unanswered';
        const likes = (q.like_count || 0) > 0 ? `, ${q.like_count} likes` : '';
        return `${text} [${status}${likes}]`;
      })
      .join(' | ');
  }

  const handle = normHandle(creator?.instagram_handle);
  if (!handle) return '';

  const tops = commentIntelligence?.top_questions || [];
  const matched = tops.filter((q) =>
    (q.creator_handles || []).some((h) => normHandle(h) === handle)
  );
  if (!matched.length) return '';

  return matched
    .map((q) => {
      const text = String(q.question || q.examples?.[0] || '').replace(/\|/g, '/').trim();
      const status = q.unanswered ? 'unanswered' : 'answered';
      return `${text} [${status}]`;
    })
    .join(' | ');
}

export function buildCampaignTopQuestionsSection(topQuestions) {
  const items = topQuestions || [];
  if (!items.length) return [];
  const rows = [
    '# campaign_top_buyer_questions',
    'rank,question,mentions,comment_likes,reply_status,creator_handles,example_quotes',
  ];
  items.forEach((q, i) => {
    const examples = (q.examples || [])
      .map((ex) => String(ex).replace(/\|/g, '/').trim())
      .filter(Boolean)
      .join(' | ');
    rows.push(
      [
        i + 1,
        q.question,
        q.frequency_estimate ?? 1,
        q.total_likes ?? 0,
        q.unanswered ? 'unanswered' : 'replied',
        (q.creator_handles || [])
          .map((h) => `@${String(h).replace(/^@/, '')}`)
          .join('; '),
        examples,
      ]
        .map(csvEscape)
        .join(',')
    );
  });
  rows.push('');
  return rows;
}

function buildCampaignSuggestedActionsSection(actionItems) {
  const items = actionItems || [];
  if (!items.length) return [];
  const rows = [
    '# campaign_suggested_actions',
    'priority,title,detail,creator_handles,suggested_crm_note',
  ];
  for (const a of items) {
    rows.push(
      [
        a.priority || 'medium',
        a.title,
        a.detail,
        (a.creator_handles || [])
          .map((h) => `@${String(h).replace(/^@/, '')}`)
          .join('; '),
        a.suggested_crm_note,
      ]
        .map(csvEscape)
        .join(',')
    );
  }
  rows.push('');
  return rows;
}

/** Actions tagged to this creator (for per-creator row). */
function suggestedActionsForCreator(creator, actionItems) {
  const handle = normHandle(creator?.instagram_handle);
  if (!handle) return '';
  const matched = (actionItems || []).filter((a) =>
    (a.creator_handles || []).some((h) => normHandle(h) === handle)
  );
  if (!matched.length) return '';
  return matched
    .map((a) => {
      const note = (a.suggested_crm_note || a.detail || '').replace(/\|/g, '/').trim();
      return note ? `${a.title} — ${note}` : a.title;
    })
    .join(' | ');
}

function unansweredQuestionsText(creator, commentIntelligence) {
  const fromRow = creator?.buyer_questions;
  if (Array.isArray(fromRow) && fromRow.length > 0) {
    return fromRow
      .filter((q) => !q.answered)
      .map((q) => String(q.text || '').replace(/\|/g, '/').trim())
      .join(' | ');
  }
  const handle = normHandle(creator?.instagram_handle);
  const tops = (commentIntelligence?.top_questions || []).filter(
    (q) =>
      q.unanswered &&
      (q.creator_handles || []).some((h) => normHandle(h) === handle)
  );
  return tops
    .map((q) => String(q.question || '').replace(/\|/g, '/').trim())
    .join(' | ');
}

export function buildCommentIntelligenceCsvRows(creators, commentIntelligence) {
  const summary = commentIntelligence?.summary || {};
  const sentiment = commentIntelligence?.sentiment || {};
  const actionItems = commentIntelligence?.action_items || [];
  const topQuestions = commentIntelligence?.top_questions || [];

  const headers = [
    'instagram_handle',
    'post_url',
    'row_id',
    'ingest_status',
    'ingested_count',
    'metrics_comment_count',
    'positive_count',
    'neutral_count',
    'negative_count',
    'positive_pct',
    'neutral_pct',
    'negative_pct',
    'theme_price_count',
    'theme_praise_count',
    'theme_questions_count',
    'theme_complaints_count',
    'theme_availability_count',
    'theme_competitor_count',
    'theme_spam_count',
    'question_count',
    'answered_count',
    'unanswered_count',
    'response_rate_pct',
    'buyer_questions_asked',
    'buyer_questions_unanswered',
    'suggested_actions_for_creator',
    'creator_status',
    'creator_status_label',
  ];

  const lines = [headers.map(csvEscape).join(',')];

  const meta = [
    `# campaign_comment_summary`,
    `headline,${csvEscape(summary.headline ?? '')}`,
    `total_comments_ingested,${csvEscape(summary.total_comments_ingested ?? '')}`,
    `posts_analyzed,${csvEscape(summary.posts_analyzed ?? summary.connected_posts ?? '')}`,
    `confidence,${csvEscape(summary.confidence ?? '')}`,
    `positive_pct,${csvEscape(sentiment.positive_pct ?? '')}`,
    `neutral_pct,${csvEscape(sentiment.neutral_pct ?? '')}`,
    `negative_pct,${csvEscape(sentiment.negative_pct ?? '')}`,
    '',
    ...buildCampaignTopQuestionsSection(topQuestions),
    ...buildCampaignSuggestedActionsSection(actionItems),
  ];

  for (const c of creators) {
    lines.push(
      [
        c.instagram_handle,
        c.post_url,
        c.row_id,
        c.ingest_status,
        rowVal(c, 'ingested_count'),
        rowVal(c, 'metrics_comment_count'),
        rowVal(c, 'positive_count'),
        rowVal(c, 'neutral_count'),
        rowVal(c, 'negative_count'),
        rowVal(c, 'positive_pct'),
        rowVal(c, 'neutral_pct'),
        rowVal(c, 'negative_pct'),
        rowVal(c, 'theme_price_count'),
        rowVal(c, 'theme_praise_count'),
        rowVal(c, 'theme_questions_count'),
        rowVal(c, 'theme_complaints_count'),
        rowVal(c, 'theme_availability_count'),
        rowVal(c, 'theme_competitor_count'),
        rowVal(c, 'theme_spam_count'),
        rowVal(c, 'question_count'),
        rowVal(c, 'answered_count'),
        rowVal(c, 'unanswered_count'),
        rowVal(c, 'response_rate_pct'),
        formatBuyerQuestionsForCsv(c, commentIntelligence),
        unansweredQuestionsText(c, commentIntelligence),
        suggestedActionsForCreator(c, actionItems),
        c.status,
        c.status_label,
      ]
        .map(csvEscape)
        .join(',')
    );
  }

  return [...meta, ...lines].join('\n');
}

export function downloadCommentIntelligenceCsv(
  creators,
  { campaignName, filterId, commentIntelligence }
) {
  const filterSlug = safeCsvSlug(
    filterLabelFromList(COMMENT_FILTERS, filterId, 'all')
  );
  const nameSlug = safeCsvSlug(campaignName);
  const filename = `performance-hub_comment-intelligence_${nameSlug}_${filterSlug}.csv`;
  const body = buildCommentIntelligenceCsvRows(creators, commentIntelligence);
  triggerCsvDownload(filename, body);
}
